import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, Lock, ArrowLeft, CheckCircle } from 'lucide-react';

// Load Stripe with your live publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51SBkVdHxGw23PkkvEUphY0QIdQL5icCxFEux9DOj94D4bvjTsg3kMFNqf9X2Orrefbbror3wRfz6v3KqF3HErTCS00EgVb4biW');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#424770',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const PaymentForm = ({ appointmentData, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setPaymentError('');

    const cardElement = elements.getElement(CardElement);

    // Create payment intent on your server
    try {
      const response = await fetch('/scheduler/api/create-payment-intent.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointment_id: appointmentData.id,
          amount: Math.round(appointmentData.final_price * 100), // Convert to cents
          currency: 'usd',
          metadata: {
            appointment_id: appointmentData.id,
            customer_name: appointmentData.customer_name,
            service_name: appointmentData.service_name,
          }
        }),
      });

      const { clientSecret, error } = await response.json();

      if (error) {
        setPaymentError(error);
        setIsProcessing(false);
        return;
      }

      // Confirm payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: appointmentData.customer_name,
            email: appointmentData.customer_email,
          },
        },
      });

      if (confirmError) {
        setPaymentError(confirmError.message);
        setIsProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        // Payment succeeded, update appointment status
        const updateResponse = await fetch('/scheduler/api/update-payment-status.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            appointment_id: appointmentData.id,
            payment_intent_id: paymentIntent.id,
            payment_status: 'paid',
            payment_method: 'Credit Card',
            payment_amount: appointmentData.final_price
          }),
        });

        const updateResult = await updateResponse.json();

        if (updateResult.success) {
          setPaymentSuccess(true);
          setTimeout(() => {
            onSuccess(paymentIntent);
          }, 2000);
        } else {
          setPaymentError('Payment processed but failed to update appointment status');
        }

        setIsProcessing(false);
      }
    } catch (error) {
      setPaymentError('Network error. Please try again.');
      setIsProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-green-800 mb-2">Payment Successful!</h3>
        <p className="text-gray-600">Redirecting you back to confirmation...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">Payment Summary</h3>
        <div className="space-y-1 text-sm text-blue-700">
          <div className="flex justify-between">
            <span>Service:</span>
            <span>{appointmentData.service_name}</span>
          </div>
          <div className="flex justify-between">
            <span>Date:</span>
            <span>{new Date(appointmentData.appointment_date + 'T00:00:00').toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Time:</span>
            <span>{appointmentData.appointment_time}</span>
          </div>
          <div className="flex justify-between font-semibold text-blue-800 border-t border-blue-200 pt-2">
            <span>Total Amount:</span>
            <span>${parseFloat(appointmentData.final_price).toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <CreditCard className="w-4 h-4 inline mr-2" />
          Card Information
        </label>
        <div className="border border-gray-300 rounded-lg p-3 bg-white">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {paymentError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{paymentError}</p>
        </div>
      )}

      <div className="flex items-center text-sm text-gray-600 mb-4">
        <Lock className="w-4 h-4 mr-2" />
        <span>Your payment information is secure and encrypted</span>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Processing...' : `Pay $${parseFloat(appointmentData.final_price).toFixed(2)}`}
        </button>
      </div>
    </form>
  );
};

const StripePayment = ({ appointmentData, onSuccess, onCancel }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
        <div className="mb-6">
          <button
            onClick={onCancel}
            className="flex items-center text-green-600 hover:text-green-700 font-medium mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Confirmation
          </button>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Complete Payment</h2>
          <p className="text-gray-600">Secure payment for your appointment</p>
        </div>

        <Elements stripe={stripePromise}>
          <PaymentForm
            appointmentData={appointmentData}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        </Elements>
      </div>
    </div>
  );
};

export default StripePayment;